import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import {
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  HeatUpSpeedCommands,
  HeatUpSpeedProperties,
  IsActiveProperty,
  ProgramCommands,
  ProgramProperties,
  RoomCo2Commands,
  RoomCo2Properties,
  RoomProperties,
  RoomSensorsCo2Properties,
  CondensationRiskProperties,
  RangeConstraints,
  Feature,
  DcSensorValueProperties,
  FeaturePropertyKey,
  ChildLockProperties,
  RoomCommands,
  TemperatureProperty,
  RoomsOperatingStateDemand,
  RoomsOperatingStateReason,
  DeviceIds,
  quickmodesProperties,
  ActivateDeactivateCommands,
  RoomOperatingStateProperties,
  PropertiesStringValueGeneric,
  ActiveModeCommandsGeneric,
} from '@vi-iot/client';

import {
  FeatureService,
  GatewayStore,
  InstallationFeaturesStore,
  isReady,
  isRemoteDeviceOfType,
  RemoteDeviceType,
  generateNumbers,
  RoomActor,
  RoomsControlMode,
} from '@store';

import { FormControl, Validators } from '@angular/forms';
import { NO_VALUE } from '@pipes';
import { PillType } from '../../../shared/components/pill/pill.model';
import { SUFFIX } from '../../../shared/common/units';
import { InstallationService } from '../../services/installation.service';

import { GTMService, UIService } from '@services';
import { MatDialog } from '@angular/material/dialog';
import { RoomModeModalComponent } from './room-mode-modal/room-mode-modal.component';

@Component({
  selector: 'app-room-panel',
  templateUrl: './room-panel.component.html',
  styleUrls: ['./room-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomPanelComponent implements OnInit, OnDestroy {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly featureCommands = FeatureCommands;
  readonly SUFFIX = SUFFIX;
  room$: Observable<Feature<RoomProperties, RoomCommands>>;
  sensorTemperature$: Observable<Feature<DcSensorValueProperties, never>>;
  childLock$: Observable<Feature<ChildLockProperties, never>>;
  configurationOpenWindow$: Observable<Feature<IsActiveProperty, never>>;
  sensorHumidity$: Observable<Feature<DcSensorValueProperties, never>>;
  sensorCo2$: Observable<Feature<RoomSensorsCo2Properties, never>>;
  roomCo2$: Observable<Feature<RoomCo2Properties, RoomCo2Commands>>;
  geofencing$: Observable<Feature<IsActiveProperty, never>>; // TODO not found in wiki
  heatUpSpeed$: Observable<Feature<HeatUpSpeedProperties, HeatUpSpeedCommands>>;
  heating$: Observable<Feature<ProgramProperties, ProgramCommands>>;
  cooling$: Observable<Feature<ProgramProperties, ProgramCommands>>;
  operatingState$: Observable<Feature<RoomOperatingStateProperties, never>>;
  condensationRisk$: Observable<Feature<CondensationRiskProperties, never>>;
  co2Concentration: FormControl<number>;
  targetTemperature$: Observable<Feature<TemperatureProperty, never>>;
  levelsHeating$: Observable<Feature<TemperatureProperty, never>>;
  levelsCooling$: Observable<Feature<TemperatureProperty, never>>;
  co2SensorDevices$: Observable<RoomActor[]>;
  roomControlMode$: Observable<
    Feature<
      PropertiesStringValueGeneric<RoomsControlMode>,
      ActiveModeCommandsGeneric<RoomsControlMode>
    >
  >;
  manualOrManualStillNextSchedule: boolean;
  isManualMode: boolean;
  isManualTillNextSchedule: boolean;

  readonly isRemoteDeviceModelOfType = isRemoteDeviceOfType;
  readonly srcType = RemoteDeviceType;
  readonly pillType = PillType;
  readonly NO_VALUE = NO_VALUE;

  ngUnsubscribe: Subject<void> = new Subject<void>();

  @Input() roomId: string;

  @Input() isExpanded: boolean;

  @Output() expand: EventEmitter<void> = new EventEmitter<void>();

  @Output() collapse: EventEmitter<void> = new EventEmitter<void>();

  private deviceId: string;
  private gatewayId: string;
  private operatingState: string;

  constructor(
    public gatewayStore: GatewayStore,
    public installationFeaturesStore: InstallationFeaturesStore,
    public uiService: UIService,
    private featureService: FeatureService,
    private installationService: InstallationService,
    private gtm: GTMService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.room$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomDetails(this.roomId),
      DeviceIds.RoomControl
    );
    this.sensorTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomTemperature(this.roomId),
      DeviceIds.RoomControl
    );
    this.childLock$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.childLock(this.roomId),
      DeviceIds.RoomControl
    );

    this.configurationOpenWindow$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.configurationOpenWindow(this.roomId),
      DeviceIds.RoomControl
    );
    this.sensorHumidity$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.roomHumidity(this.roomId),
      DeviceIds.RoomControl
    );
    this.sensorCo2$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      // contains actual ppm value
      FeatureNames.roomSensorsCo2(this.roomId),
      DeviceIds.RoomControl
    );
    this.roomCo2$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      // contains desired ppm value + commands to set it
      FeatureNames.roomCo2(this.roomId),
      DeviceIds.RoomControl
    );

    this.geofencing$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsActiveProperty,
      never
    >(FeatureNames.roomGeofencing(this.roomId), DeviceIds.RoomControl);

    this.condensationRisk$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.condensationRisk(this.roomId),
      DeviceIds.RoomControl
    );

    this.heatUpSpeed$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      HeatUpSpeedProperties,
      HeatUpSpeedCommands
    >(FeatureNames.roomHeatUpSpeed(this.roomId), DeviceIds.RoomControl);

    this.operatingState$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      RoomOperatingStateProperties,
      never
    >(FeatureNames.roomOperatingState(this.roomId), DeviceIds.RoomControl);

    this.levelsHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      TemperatureProperty,
      never
    >(FeatureNames.roomLevelsHeating(this.roomId), DeviceIds.RoomControl);

    this.levelsCooling$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      TemperatureProperty,
      never
    >(FeatureNames.roomLevelsCooling(this.roomId), DeviceIds.RoomControl);

    this.getTargetRoomTemperature();
    this.initActiveProgramSubscription();

    this.co2Concentration = new FormControl(null, [Validators.required, Validators.min(1)]);

    this.roomCo2$
      .pipe(
        filter((feature) => Boolean(feature)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((feature) => {
        this.co2Concentration.setValue(feature.properties?.concentration?.value, {
          emitEvent: false,
        });
        this.co2Concentration.markAsTouched();
      });

    this.co2Concentration.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value) => this.onTargetPPMChange(value));

    this.co2SensorDevices$ = this.installationFeaturesStore.getDevicesOfChosenTypeAssignedToTheRoom(
      this.roomId,
      this.srcType.AIR_QUALITY_SENSOR
    );

    this.roomControlMode$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      PropertiesStringValueGeneric<RoomsControlMode>,
      ActiveModeCommandsGeneric<RoomsControlMode>
    >(FeatureNames.roomControlMode(this.roomId), DeviceIds.RoomControl);

    this.installationFeaturesStore
      .selectInstallationDeviceFeature<quickmodesProperties, ActivateDeactivateCommands>(
        FeatureNames.roomQuickModesManualTillNextSchedule(this.roomId),
        DeviceIds.RoomControl
      )
      .pipe(
        filter((feature) => isReady(feature)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((feature) => {
        this.isManualTillNextSchedule = feature.commands?.deactivate?.isExecutable;
      });

    this.installationFeaturesStore
      .selectInstallationDeviceFeature<quickmodesProperties, ActivateDeactivateCommands>(
        FeatureNames.roomQuickModesManual(this.roomId),
        DeviceIds.RoomControl
      )
      .pipe(
        filter((feature) => isReady(feature)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((feature) => {
        this.isManualMode = feature.properties?.active?.value;
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onConfigurationOpenWindow(value: boolean): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.configurationOpenWindow(this.roomId),
      value,
      FeaturePropertyKey.active,
      null,
      value ? FeatureCommands.activate : FeatureCommands.deactivate,
      null,
      DeviceIds.RoomControl
    );
  }

  protected selectRoom(): void {
    this.installationService.selectedRoomId$.next(this.roomId);
  }

  protected onRoomTypeChange(value: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.roomDetails(this.roomId),
      value,
      FeatureActionFields.type,
      FeatureActionFields.type,
      FeatureCommands.setType,
      null,
      DeviceIds.RoomControl
    );
  }

  protected onHeatUpSpeedChange(value: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.roomHeatUpSpeed(this.roomId),
      value,
      FeaturePropertyKey.value,
      null,
      FeatureCommands.setType,
      null,
      DeviceIds.RoomControl
    );
  }

  protected targetPPMRange(constraints: RangeConstraints): number[] {
    const ppmRangeArray = generateNumbers(constraints.min, constraints.max, constraints.stepping);

    return ppmRangeArray;
  }

  onRoomControlModeChange(value: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.roomControlMode(this.roomId),
      value,
      FeaturePropertyKey.value,
      FeatureActionFields.mode,
      FeatureCommands.setMode,
      null,
      DeviceIds.RoomControl
    );
  }
  private onTargetPPMChange(value: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.roomCo2(this.roomId),
      value,
      this.featurePropertyKey.value,
      FeatureActionFields.concentration,
      FeatureCommands.setConcentration,
      null,
      DeviceIds.RoomControl
    );
  }

  private initActiveProgramSubscription(): void {
    this.operatingState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => isReady(feature)),
        filter((feature) => !!feature?.properties?.level?.value),
        map((feature) => feature.properties.level.value)
      )
      .subscribe((activeProgram) => {
        const srcOperatingProgramHeating = this.featureService.getSRCOperatingProgramHeating(
          activeProgram
        );
        const srcOperatingProgramCooling = this.featureService.getSRCOperatingProgramCooling(
          activeProgram
        );

        this.heating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
          FeatureNames.srcOperatingProgram(this.roomId, srcOperatingProgramHeating, false),
          DeviceIds.RoomControl
        );
        this.cooling$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
          FeatureNames.srcOperatingProgram(this.roomId, srcOperatingProgramCooling, false),
          DeviceIds.RoomControl
        );
      });
  }

  private getTargetRoomTemperature(): void {
    this.operatingState$
      .pipe(
        filter((feature) => isReady(feature)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((feature) => {
        const demand = feature.properties.demand.value;
        const reason = feature.properties.reason.value;
        this.deviceId = feature.deviceId;
        this.gatewayId = feature.gatewayId;
        this.operatingState = feature.properties.reason.value;
        if (this.operatingState === 'manual' || this.operatingState === 'manualTillNextSchedule') {
          this.manualOrManualStillNextSchedule = true;
        }
        if (demand === RoomsOperatingStateDemand.cooling) {
          this.targetTemperature$ = this.levelsCooling$;
        } else if (reason === RoomsOperatingStateReason.standby) {
          return;
        } else {
          this.targetTemperature$ = this.levelsHeating$;
        }
      });
  }

  triggerManualMode(): void {
    this.gtm.track('onChange', 'Change SRC manual mode', 'SRC manual mode');

    this.installationFeaturesStore.updateFeature(
      this.isManualMode
        ? FeatureNames.roomQuickModesManual(this.roomId)
        : FeatureNames.roomQuickModesManualTillNextSchedule(this.roomId),
      true,
      this.featurePropertyKey.active,
      '',
      FeatureCommands.deactivate,
      this.gatewayId,
      this.deviceId
    );
  }

  cancelTriggerManualMode(): void {
    this.gtm.track('onCancel', 'Cancel SRC manual mode', 'SRC manual mode');
  }

  openRoomModeModal(): void {
    this.dialog.open(RoomModeModalComponent, {
      width: '535px',
      disableClose: true,
      panelClass: 'room-mode-modal',
    });
  }
}
